import React, { useState, useEffect, Fragment } from 'react';
import { ExportOrdersData, getAllOrders, UpdateOrderStatus, getOrderSummary } from '../../../api-handle/orderApi';
import { Accordion, AccordionItem, AccordionHeader, AccordionBody, Button, Select, Input, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import { User, Package, DollarSign, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { PackageCheck } from 'lucide-react';
import { Breadcrumbs } from '../../../AbstractElements';
import OrderSummaryCards from './OrderSummaryCard';
import ExportDropdown from './ExportDropDown';
import { Trash, Truck } from 'react-feather';

function OrderManagement() {
    const [orders, setOrders] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [orderSummary, setOrderSummary] = useState(null);
    const [showCancelInput, setShowCancelInput] = useState(false);
    const [cancelledReason, setCancelledReason] = useState("");
    const [error, setError] = useState("");
    const FILE_BASE_URL = process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL;

    useEffect(() => {
        fetchOrders();
        fetchOrdersSummary();
    }, [statusFilter, startDate, endDate, currentPage]);

    const resetFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
        setStatusFilter("");
    };

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const queryParams = {};
            if (statusFilter) queryParams.status = statusFilter;
            if (startDate) queryParams.startDate = new Date(startDate).toISOString();
            if (endDate) queryParams.endDate = new Date(endDate).toISOString();
            queryParams.page = currentPage;
            queryParams.size = 5;

            const response = await getAllOrders(queryParams);
            setOrders(response.orders);
            setTotalPages(response.pagination.totalPages);
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrdersSummary = async () => {
        try {
            const response = await getOrderSummary();
            setOrderSummary(response.data);
        } catch (error) {
            console.error("Error fetching order summary:", error);
        }
    };

    const handleUpdateStatus = async (orderId, status) => {
        try {
            await UpdateOrderStatus(orderId, { status, cancelledReason });
            fetchOrders();
        } catch (error) {
            console.error("Error updating order status:", error);
        }
    };

    const handleExportOrders = async () => {
        try {
            await ExportOrdersData();
        } catch (error) {
            console.error("Error exporting orders:", error);
        }
    };

    const toggleAccordion = (orderId) => {
        setActiveAccordion((prev) => (prev === orderId ? null : orderId));
    };

    const [width, setWidth] = useState(window.innerWidth);

    useEffect(() => {
        const onResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
    }, [cancelledReason]);

    let gridTemplateColumns = '1fr';
    if (width >= 640 && width < 1024) gridTemplateColumns = '1fr 1fr';
    else if (width >= 1024) gridTemplateColumns = 'repeat(5, 1fr)';

    const gridStyle = {
        display: 'grid',
        gridTemplateColumns,
        gap: '1rem',
        marginBottom: '1.5rem',
        alignItems: 'center',
    };

    const inputStyle = {
        width: '100%',
        height: '48px',
        padding: '0 12px',
        border: '1px solid #ccc',
        borderRadius: '12px',
        fontSize: '14px',
        boxSizing: 'border-box',
    };

    const buttonStyle = {
        width: '100%',
        height: '48px',
        backgroundColor: '#33bdbf',
        color: '#fff',
        border: 'none',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '14px',
        cursor: 'pointer',
    };

    const noData = !orders || orders.length === 0;
    const noPages = totalPages === 0;
    const disablePagination = noData || noPages || currentPage >= totalPages;

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Order Management" parent="Order Manager" title="Order History & Revenue" />
            {orderSummary && <OrderSummaryCards data={orderSummary} />}
            <div className="order-management-container">
                <div style={gridStyle}>
                    {/* Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="">All Orders</option>
                        <option value="placed">Placed</option>
                        <option value="ongoing">Ongoing</option>
                        <option value="out_for_delivery">Out for Delivery</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="received">Received</option>
                    </select>

                    {/* Start Date */}
                    <input
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => setStartDate(e.target.value || null)}
                        placeholder="Start Date"
                        style={inputStyle}
                    />

                    {/* End Date */}
                    <input
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => setEndDate(e.target.value || null)}
                        placeholder="End Date"
                        style={inputStyle}
                    />

                    {/* Export Button */}
                    <button style={buttonStyle}>
                        <ExportDropdown />
                    </button>

                    <button onClick={resetFilters} color="secondary" style={{ ...buttonStyle, backgroundColor: "#ef4444" }}>
                        <Trash size={18} /> Reset Filters
                    </button>
                </div>

                {loading ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px',
                        }}
                    >
                        <Loader2
                            size={48}
                            color="#33bdbf"
                            style={{
                                animation: 'spin 1s linear infinite',
                                filter: 'drop-shadow(0 0 6px # SENSOR )',
                                transform: 'scale(1.1)',
                            }}
                        />
                        <style>
                            {`
                                @keyframes spin {
                                    from { transform: rotate(0deg); }
                                    to { transform: rotate(360deg); }
                                }
                            `}
                        </style>
                    </div>
                ) : (
                    <Accordion open={activeAccordion} toggle={toggleAccordion}>
                        {orders.map((order) => (
                            <AccordionItem
                                key={order._id}
                                style={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    marginBottom: '16px',
                                    overflow: 'hidden',
                                }}
                            >
                                <AccordionHeader
                                    targetId={order._id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        justifyContent: "space-between",
                                        padding: "16px",
                                        borderLeft: `4px solid ${order.status === "completed" ? "#10b981" :
                                            order.status === "cancelled" ? "#ef4444" :
                                                order.status === "placed" ? "#3b82f6" :
                                                    order.status === "ongoing" ? "#8b5cf6" :
                                                        order.status === "out_for_delivery" ? "#f59e0b" :
                                                            order.status === "received" ? "#14b8a6" : // 🆕 Teal shade
                                                                "#6b7280"   // Default (gray)
                                            }`,
                                        backgroundColor: "#f9fafb",
                                        fontWeight: "600",
                                        fontSize: "16px",
                                        gap: "16px",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", flex: 1, minWidth: "200px" }}>
                                        <PackageCheck size={20} color="#4B5563" />
                                        <span>Order ID: {order.uniqueId || order._id}</span>
                                        <span>Customer: <strong>{order.user?.name || "N/A"}</strong></span>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span
                                            style={{
                                                backgroundColor:
                                                    order.status === "completed" ? "#d1fae5" :
                                                        order.status === "cancelled" ? "#fee2e2" :
                                                            order.status === "placed" ? "#dbeafe" :
                                                                order.status === "ongoing" ? "#ede9fe" :
                                                                    order.status === "out_for_delivery" ? "#fef9c3" :
                                                                        order.status === "received" ? "#ccfbf1" : // 🆕 light teal
                                                                            "#e5e7eb",

                                                color:
                                                    order.status === "completed" ? "#065f46" :
                                                        order.status === "cancelled" ? "#991b1b" :
                                                            order.status === "placed" ? "#1e40af" :
                                                                order.status === "ongoing" ? "#5b21b6" :
                                                                    order.status === "out_for_delivery" ? "#92400e" :
                                                                        order.status === "received" ? "#0f766e" : // 🆕 deep teal
                                                                            "#4b5563",
                                                padding: "4px 12px",
                                                borderRadius: "9999px",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                minWidth: "80px",
                                                textAlign: "center",
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {order.status}
                                        </span>
                                    </div>
                                </AccordionHeader>
                                <AccordionBody accordionId={order._id} style={{ padding: "20px" }}>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                                            gap: "20px",
                                            marginBottom: "20px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                backgroundColor: "#f3f4f6",
                                                padding: "20px",
                                                borderRadius: "8px",
                                                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                                            }}
                                        >
                                            <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "10px", color: "#333" }}>
                                                <User size={20} color="#4B5563" style={{ marginRight: "8px" }} />
                                                Customer Info
                                            </h3>
                                            <p><strong>Name:</strong> {order?.user?.name || "N/A"}</p>
                                            <p><strong>Email:</strong> {order.user?.email}</p>
                                            <p><strong>Address:</strong> {order.shippingAddressSnapshot?.name}, {order.shippingAddressSnapshot?.location}</p>
                                        </div>
                                        <div
                                            style={{
                                                backgroundColor: "#f3f4f6",
                                                padding: "20px",
                                                borderRadius: "8px",
                                                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                                            }}
                                        >
                                            <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "10px", color: "#333" }}>
                                                <Package size={20} color="#4B5563" style={{ marginRight: "8px" }} />
                                                Order Info
                                            </h3>
                                            <p><strong>Total Amount:</strong> KWD {order?.totalAmount?.toFixed(2)}</p>
                                            <p><strong>Payment Status:</strong> {order.paymentStatus}</p>
                                            <p><strong>Delivery Type:</strong> {order.deliveryType}</p>
                                            <p><strong>Placed On:</strong> {new Date(order.createdAt).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            backgroundColor: "#f3f4f6",
                                            padding: "20px",
                                            borderRadius: "8px",
                                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                                            marginBottom: "20px",
                                        }}
                                    >
                                        <h4 style={{ fontSize: "15px", fontWeight: "600", marginBottom: "10px", color: "#333" }}>
                                            <DollarSign size={20} color="#4B5563" style={{ marginRight: "8px" }} />
                                            Order Summary
                                        </h4>
                                        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                                            {order.orderItems.map((item) => (
                                                <li
                                                    key={item._id}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                        marginBottom: "8px",
                                                    }}
                                                >
                                                    <img
                                                        src={`${FILE_BASE_URL}/${item.product.images[0]}`}
                                                        alt={item.product?.name}
                                                        style={{
                                                            width: "50px",
                                                            height: "50px",
                                                            objectFit: "cover",
                                                            borderRadius: "8px",
                                                        }}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        <strong>{item.quantity}x {item.product?.name}</strong> — KWD {item.price.toFixed(2)}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div style={{ marginTop: "20px" }}>
                                        {order.status === "placed" && (
                                            <>
                                                {!showCancelInput ? (
                                                    <div style={{ display: "flex", gap: "10px" }}>
                                                        <button
                                                            onClick={() => handleUpdateStatus(order._id, "ongoing")}
                                                            style={{
                                                                backgroundColor: "#10b981",
                                                                color: "white",
                                                                padding: "12px 24px",
                                                                border: "none",
                                                                borderRadius: "8px",
                                                                cursor: "pointer",
                                                                fontWeight: "600",
                                                                flex: 1,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <CheckCircle size={20} style={{ marginRight: "8px" }} />
                                                            Mark as Ongoing
                                                        </button>
                                                        <button
                                                            onClick={() => setShowCancelInput(true)}
                                                            style={{
                                                                backgroundColor: "#ef4444",
                                                                color: "white",
                                                                padding: "12px 24px",
                                                                border: "none",
                                                                borderRadius: "8px",
                                                                cursor: "pointer",
                                                                fontWeight: "600",
                                                                flex: 1,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <XCircle size={20} style={{ marginRight: "8px" }} />
                                                            Cancel Order
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                                        <textarea
                                                            placeholder="Enter reason for cancellation"
                                                            value={cancelledReason}
                                                            onChange={(e) => setCancelledReason(e.target.value)}
                                                            style={{
                                                                width: "100%",
                                                                minHeight: "80px",
                                                                padding: "12px",
                                                                borderRadius: "8px",
                                                                border: "1px solid #d1d5db",
                                                                resize: "none",
                                                                fontSize: "14px",
                                                            }}
                                                        />
                                                        {error && (
                                                            <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "-5px" }}>
                                                                {error}
                                                            </p>
                                                        )}
                                                        <div style={{ display: "flex", gap: "10px" }}>
                                                            <button
                                                                onClick={() => {
                                                                    if (!cancelledReason.trim()) {
                                                                        setError("Cancellation reason is required");
                                                                        return;
                                                                    }
                                                                    setError("");
                                                                    handleUpdateStatus(order._id, "cancelled");
                                                                    setShowCancelInput(false);
                                                                    setCancelledReason("");
                                                                }}
                                                                style={{
                                                                    backgroundColor: "#ef4444",
                                                                    color: "white",
                                                                    padding: "12px 24px",
                                                                    border: "none",
                                                                    borderRadius: "8px",
                                                                    cursor: cancelledReason.trim() ? "pointer" : "not-allowed",
                                                                    fontWeight: "600",
                                                                    flex: 1,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    opacity: cancelledReason.trim() ? 1 : 0.6,
                                                                }}
                                                                disabled={!cancelledReason.trim()}
                                                            >
                                                                <CheckCircle size={20} style={{ marginRight: "8px" }} />
                                                                Submit Cancellation
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setShowCancelInput(false);
                                                                    setCancelledReason("");
                                                                    setError("");
                                                                }}
                                                                style={{
                                                                    backgroundColor: "#6b7280",
                                                                    color: "white",
                                                                    padding: "12px 24px",
                                                                    border: "none",
                                                                    borderRadius: "8px",
                                                                    cursor: "pointer",
                                                                    fontWeight: "600",
                                                                    flex: 1,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                <XCircle size={20} style={{ marginRight: "8px" }} />
                                                                Back
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {order.status === "ongoing" && (
                                            <button
                                                onClick={() => handleUpdateStatus(order._id, "out_for_delivery")}
                                                style={{
                                                    backgroundColor: "#10b981",
                                                    color: "white",
                                                    padding: "12px 24px",
                                                    border: "none",
                                                    borderRadius: "8px",
                                                    cursor: "pointer",
                                                    fontWeight: "600",
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Truck size={20} style={{ marginRight: "8px" }} />
                                                Mark as Out for Delivery
                                            </button>
                                        )}
                                        {order.status === "out_for_delivery" && (
                                            <button
                                                onClick={() => handleUpdateStatus(order._id, "completed")}
                                                style={{
                                                    backgroundColor: "#10b981",
                                                    color: "white",
                                                    padding: "12px 24px",
                                                    border: "none",
                                                    borderRadius: "8px",
                                                    cursor: "pointer",
                                                    fontWeight: "600",
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <CheckCircle size={20} style={{ marginRight: "8px" }} />
                                                Mark as Completed
                                            </button>
                                        )}
                                        {order.status === "cancelled" && (
                                            <textarea
                                                placeholder="Reason for cancellation"
                                                value={order?.cancelledReason ? `Reason: ${order.cancelledReason}` : "Reason: No Reason Provided"}
                                                readOnly
                                                style={{
                                                    width: "100%",
                                                    minHeight: "80px",
                                                    padding: "12px",
                                                    borderRadius: "8px",
                                                    border: "1px solid #d1d5db",
                                                    resize: "none",
                                                    fontSize: "14px",
                                                    backgroundColor: "#f3f4f6",
                                                    cursor: "default",
                                                }}
                                            />
                                        )}
                                    </div>
                                </AccordionBody>
                            </AccordionItem>
                        ))}
                    </Accordion>
                )}
                {!loading && (
                    <div className="d-flex justify-content-center mt-3">
                        <Pagination>
                            <PaginationItem disabled={currentPage === 1}>
                                <PaginationLink first onClick={() => setCurrentPage(1)} />
                            </PaginationItem>
                            <PaginationItem disabled={currentPage === 1}>
                                <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                            </PaginationItem>
                            <PaginationItem disabled={disablePagination}>
                                <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                            </PaginationItem>

                            <PaginationItem disabled={disablePagination}>
                                <PaginationLink last onClick={() => setCurrentPage(totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </Fragment>
    );
}

export default OrderManagement;